<template>
  <v-container>
    <v-row class="pt-10 pb-2" justify="center">
      <v-col cols="12" md="6" class="text-center">
        <div class="mb-4">
          <v-img v-if="logoSrc" :src="logoSrc" alt="App logo" max-width="120" class="app-logo-image mx-auto" contain />
          <v-avatar v-else size="72" class="app-logo">
            <span class="app-logo-initial">{{ appDisplayInitial }}</span>
          </v-avatar>
        </div>
        <h1 class="mb-2">{{ appDisplayName }}</h1>
        <div class="subtitle-1 mb-1">Information on how to delete your account and associated data.</div>
        <div class="body-2 mt-3 text-left info-text">
          <p>
            At autosense, we take your data privacy seriously. If you no longer wish to use the
            <b>{{ appDisplayName }}</b> app, you can request the permanent deletion of your account and all associated
            personal data through this page.
          </p>
          <p class="mb-1 font-weight-bold">Important Information regarding Data Deletion:</p>
          <ul>
            <li>
              <strong>Permanent Action:</strong>
              Deleting your account is permanent. Your profile, history, and preferences will be removed.
            </li>
            <li>
              <strong>Data Retention:</strong>
              Please note that we are required by law to retain certain information (e.g., transaction records,
              invoices, or data required for security and fraud prevention) for a specific period as defined by legal
              and regulatory requirements. For more details, please refer to our
              <a :href="privacyPolicyUrl" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </li>
          </ul>
        </div>
      </v-col>
    </v-row>

    <v-row justify="center">
      <v-col cols="12" md="6">
        <v-card elevation="10" class="rounded-card pa-4">
          <v-form ref="form" v-model="isValid" lazy-validation>
            <template v-if="!successMessage">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model="firstName" label="First name" :rules="firstNameRules" required />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="lastName" label="Last name" :rules="lastNameRules" required />
                </v-col>
              </v-row>

              <v-text-field
                v-model="email"
                label="Email (same as used for login in the app)"
                type="email"
                :rules="emailRules"
                required
              />

              <v-text-field v-model="phone" label="Phone Number" :rules="phoneRules" required />

              <v-select v-model="reason" :items="reasonOptions" label="Reason for deletion (optional)" clearable />

              <v-checkbox v-model="confirmed">
                <template #label>
                  <span>
                    I understand that submitting this form will
                    <strong>permanently delete</strong>
                    my account.
                  </span>
                </template>
              </v-checkbox>

              <v-row justify="center" class="mt-2 mb-6">
                <v-btn color="primary" :loading="submitting" :disabled="!canSubmit" @click="submit">
                  Submit deletion request
                </v-btn>
              </v-row>
            </template>

            <v-alert v-if="errorMessage" type="error" dense text class="mb-4">
              {{ errorMessage }}
            </v-alert>

            <v-alert v-if="successMessage" type="success" dense text class="mb-4">
              {{ successMessage }}
            </v-alert>
          </v-form>
        </v-card>
      </v-col>
    </v-row>

    <v-row justify="center" class="mt-8 mb-4">
      <v-col cols="12" class="text-center footer-link">
        <a href="https://www.autosense.ch" target="_blank" rel="noopener noreferrer">www.autosense.ch</a>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { isValidPhoneNumber } from 'libphonenumber-js';
import executeAPI from '../services/execute-api';
import config from '../config';

const recaptchaSiteKey = config.recaptcha.site_key;

const appConfigMap = {
  'z-volt': {
    appId: 'zvolt',
    displayName: 'Zurich Z Volt',
    logoSrc: '/images/z-volt.png',
    privacyPolicyUrl: 'https://www.zurich.ch/en/data-protection/privacy-policy-zurich-switzerland-services-ltd',
  },
  'charge-on': {
    appId: 'volton',
    displayName: 'chargeOn',
    logoSrc: '/images/charge-on.png',
    privacyPolicyUrl: 'https://www.autosense.ch/en/datenschutz/chargeon',
  },
  mazda: {
    appId: 'mymazda',
    displayName: 'Mazda',
    logoSrc: '/images/mazda.png',
    privacyPolicyUrl: 'https://as-privacy-policy-public.s3.eu-west-1.amazonaws.com/mymazda/',
  },
  migrol: {
    appId: 'migrol',
    displayName: 'Migrolcard',
    logoSrc: '/images/migrolcard.png',
    privacyPolicyUrl:
      'https://www.migrol.ch/-/media/migrol-website/doc/mobilitaet/migrolcard/migrolcard-app/2021_dse_migrolcardapp_de.pdf',
  },
  conneqtech: {
    appId: 'starlight',
    displayName: 'Conneqtech',
    logoSrc: '/images/conneqtech.png',
    privacyPolicyUrl: 'https://autoconneqt.ch/en/privacy-policy',
  },
  amag: {
    appId: 'amag',
    displayName: 'AMAG',
    logoSrc: '/images/amag.png',
    privacyPolicyUrl: 'https://www.amag.ch/en/datenschutz/app.html',
  },
  volenergy: {
    appId: 'volenergy',
    displayName: 'Volenergy',
    logoSrc: '/images/volenergy.png',
    privacyPolicyUrl: '',
  },
};

export default {
  name: 'AccountDeletion',
  props: {
    appName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      reason: '',
      confirmed: false,
      submitting: false,
      isValid: false,
      errorMessage: '',
      successMessage: '',
      recaptchaReady: false,
      reasonOptions: ['Privacy concerns', 'No longer using the service', 'Other'],
      emailRules: [
        (v) => !!v || 'Email is required',
        (v) =>
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            v,
          ) || 'E-mail must be valid',
      ],
      phoneRules: [
        (v) => !!v || 'Phone is required',
        (v) =>
          !v ||
          isValidPhoneNumber(v) ||
          'Please enter a valid phone number in international format (e.g. +41 79 123 45 67)',
      ],
      firstNameRules: [(v) => !!v || 'First name is required'],
      lastNameRules: [(v) => !!v || 'Last name is required'],
    };
  },
  computed: {
    appConfig() {
      return appConfigMap[this.appName] || {};
    },
    appDisplayName() {
      return this.appConfig.displayName || this.appName;
    },
    appDisplayInitial() {
      return this.appDisplayName.charAt(0).toUpperCase();
    },
    logoSrc() {
      return this.appConfig.logoSrc || '';
    },
    privacyPolicyUrl() {
      return this.appConfig.privacyPolicyUrl || 'https://www.autosense.ch/datenschutz';
    },
    canSubmit() {
      return this.isValid && this.confirmed && !this.submitting;
    },
    appId() {
      return this.appConfig.appId || this.appName;
    },
  },
  mounted() {
    document.title = `Account Deletion - ${this.appDisplayName}`;

    if (!recaptchaSiteKey) {
      return;
    }

    if (window.grecaptcha) {
      this.recaptchaReady = true;
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.recaptchaReady = true;
    };

    document.head.appendChild(script);
  },
  beforeDestroy() {
    document.title = 'Account Deletion';
  },
  methods: {
    async submit() {
      this.errorMessage = '';
      this.successMessage = '';

      const form = this.$refs.form;
      if (form && typeof form.validate === 'function') {
        const valid = form.validate();
        if (!valid) {
          return;
        }
      }

      if (!this.confirmed) {
        this.errorMessage = 'Please confirm that you understand the account will be deleted.';
        return;
      }

      if (!recaptchaSiteKey) {
        this.errorMessage = 'reCAPTCHA is not configured. Please contact support.';
        return;
      }

      if (!window.grecaptcha || !this.recaptchaReady) {
        this.errorMessage = 'reCAPTCHA is still loading. Please try again in a moment.';
        return;
      }

      this.submitting = true;

      try {
        const recaptchaToken = await window.grecaptcha.execute(recaptchaSiteKey, {
          action: `account_deletion_${this.appId}`,
        });

        const language = navigator.language || 'en';
        await executeAPI(
          'post',
          '/v1/helpdesk/public/create',
          {
            first_name: this.firstName || '',
            last_name: this.lastName || '',
            reason: 'account_deletion_request',
            subject: `Account Deletion Request`,
            message: `Internal Note: This request was generated by a form on ${window.location.href} for app ${this.appDisplayName}\nReason for deletion: ${this.reason}\nUnderstood deletion: ${this.confirmed}`,
            email: this.email,
            phone: this.phone,
            request_type: 'support',
            recaptcha_token: recaptchaToken,
          },
          language,
          this.appId,
        );

        this.successMessage = `Thank you. Your request for the deletion of your ${this.appDisplayName} account has been received. Our support team will process your request within 20 business days. You will receive a final confirmation email once the process is complete.`;
        this.firstName = '';
        this.lastName = '';
        this.email = '';
        this.phone = '';
        this.reason = '';
        this.confirmed = false;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.errorMessage = 'There is no account with that email.';
        } else {
          this.errorMessage = 'Failed to submit your request. Please try again later or contact support.';
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.rounded-card {
  border-radius: 20px;
}

.app-logo {
  background-image: linear-gradient(135deg, rgb(55, 150, 131), rgb(92, 219, 149));
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.app-logo-initial {
  font-size: 2rem;
  font-weight: 600;
}

.app-logo-image {
  border-radius: 16px;
}

.info-text ul {
  padding-left: 1.2rem;
}

.footer-link a {
  color: #2c3e50;
  text-decoration: none;
}

.footer-link a:hover {
  text-decoration: underline;
}
</style>
